/**
 * ContentHubItemDetail Page
 * Displays full details of a content item including preview, metadata, and usage
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Copy,
  Share2,
  History,
  Tag,
  ExternalLink,
  MoreVertical,
  AlertCircle,
  FileText,
  Hash,
  Image as ImageIcon,
  Table as TableIcon,
  Mail,
  Presentation,
  Move,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FluentCard } from '@/components/layout/page-header'
import { PermissionGate } from '@/components/auth/permission-gate'
import { useToast } from '@/components/ui/use-toast'
import {
  ContentItemPreview,
  ContentItemMetadata,
  DeleteItemDialog,
  MoveItemDialog,
  DuplicateItemDialog,
} from '@/components/content-hub'
import {
  useContentItem,
  useContentItemUsage,
  useDeleteContentItem,
} from '@/lib/hooks/use-content-hub'
import type { ContentItemType, ContentItemDetail } from '@/lib/api/types'

// ============================================
// Types & Constants
// ============================================

const TYPE_ICONS: Record<ContentItemType, React.ElementType> = {
  Text: FileText,
  DataPoint: Hash,
  Image: ImageIcon,
  Table: TableIcon,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const TYPE_COLORS: Record<ContentItemType, string> = {
  Text: 'from-blue-400 to-blue-600',
  DataPoint: 'from-green-400 to-green-600',
  Image: 'from-purple-400 to-purple-600',
  Table: 'from-amber-400 to-amber-600',
  EmailSnippet: 'from-rose-400 to-rose-600',
  SlideElement: 'from-orange-400 to-orange-600',
}

const TYPE_LABELS: Record<ContentItemType, string> = {
  Text: 'Text Element',
  DataPoint: 'Data Point',
  Image: 'Image',
  Table: 'Table',
  EmailSnippet: 'Email Snippet',
  SlideElement: 'Slide Element',
}

// ============================================
// Loading State Component
// ============================================

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="h-6 w-40 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-2xl bg-neutral-200 dark:bg-neutral-700 animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-8 w-64 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
          <div className="h-4 w-96 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 h-96 bg-neutral-200 dark:bg-neutral-700 rounded-xl animate-pulse" />
        <div className="h-96 bg-neutral-200 dark:bg-neutral-700 rounded-xl animate-pulse" />
      </div>
    </div>
  )
}

// ============================================
// Error State Component
// ============================================

interface ErrorStateProps {
  message: string
}

function ErrorState({ message }: ErrorStateProps) {
  const navigate = useNavigate()

  return (
    <div className="space-y-6">
      <Link
        to="/content-hub"
        className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Content Hub
      </Link>

      <FluentCard className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
          Error Loading Content
        </h2>
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{message}</p>
        <Button onClick={() => navigate('/content-hub')} className="mt-4 rounded-full">
          Go to Content Hub
        </Button>
      </FluentCard>
    </div>
  )
}

// ============================================
// Tags Section Component
// ============================================

interface TagsSectionProps {
  tags: string[]
  onEditTags?: () => void
}

function TagsSection({ tags, onEditTags }: TagsSectionProps) {
  return (
    <FluentCard>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Tags</h2>
        {onEditTags && (
          <Button variant="ghost" size="sm" onClick={onEditTags} className="rounded-lg text-xs">
            Edit
          </Button>
        )}
      </div>

      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
            >
              <Tag className="h-3 w-3" />
              {tag}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">No tags added yet</p>
      )}
    </FluentCard>
  )
}

// ============================================
// Usage Tab Component
// ============================================

interface UsageTabProps {
  usageHistory: ContentItemDetail['usageHistory']
}

function UsageTab({ usageHistory }: UsageTabProps) {
  if (!usageHistory || usageHistory.length === 0) {
    return (
      <div className="text-center py-8">
        <FileText className="h-10 w-10 mx-auto text-neutral-300 dark:text-neutral-600" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-3">
          This content has not been used in any templates yet
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {usageHistory.map((usage) => (
        <div
          key={usage.id}
          className="flex items-center justify-between p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-neutral-900 dark:text-white">{usage.templateName}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Used by {usage.usedByName} on {new Date(usage.usedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <Link
            to={`/templates/${usage.templateId}`}
            className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
          >
            <ExternalLink className="h-4 w-4 text-neutral-400" />
          </Link>
        </div>
      ))}
    </div>
  )
}

// ============================================
// Versions Tab Component (Placeholder)
// ============================================

interface VersionsTabProps {
  version: number
}

function VersionsTab({ version }: VersionsTabProps) {
  // Placeholder for version history
  const versions = [
    {
      id: 'v' + version,
      versionNumber: version,
      createdAt: new Date().toISOString(),
      createdBy: 'Current User',
      changeDescription: 'Current version',
    },
  ]

  return (
    <div className="space-y-3">
      {versions.map((ver, index) => (
        <div
          key={ver.id}
          className={cn(
            'flex items-start justify-between p-4 rounded-xl border',
            index === 0
              ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
              : 'bg-neutral-50 dark:bg-neutral-800/50 border-neutral-200 dark:border-neutral-700'
          )}
        >
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'p-2 rounded-lg',
                index === 0 ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-neutral-100 dark:bg-neutral-800'
              )}
            >
              <History
                className={cn(
                  'h-4 w-4',
                  index === 0 ? 'text-blue-600 dark:text-blue-400' : 'text-neutral-400'
                )}
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-neutral-900 dark:text-white">
                  Version {ver.versionNumber}
                </p>
                {index === 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                    Current
                  </span>
                )}
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-0.5">
                {ver.changeDescription}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                {new Date(ver.createdAt).toLocaleString()} by {ver.createdBy}
              </p>
            </div>
          </div>
          {index !== 0 && (
            <Button variant="ghost" size="sm" className="rounded-lg">
              Restore
            </Button>
          )}
        </div>
      ))}
    </div>
  )
}

// ============================================
// Main Component
// ============================================

export default function ContentHubItemDetail() {
  const { itemId } = useParams<{ itemId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)

  // Fetch item data
  const {
    data: item,
    isLoading: itemLoading,
    error: itemError,
  } = useContentItem(itemId || '', {
    enabled: !!itemId,
  })

  // Fetch usage stats
  const { data: usageStats } = useContentItemUsage(itemId || '', {
    enabled: !!itemId,
  })

  // Delete mutation
  const deleteItem = useDeleteContentItem({
    onSuccess: () => {
      toast({
        title: 'Content deleted',
        description: 'The content item has been deleted successfully.',
      })
      navigate('/content-hub')
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete content item',
        variant: 'destructive',
      })
    },
  })

  // Loading state
  if (itemLoading) {
    return <LoadingState />
  }

  // Error state
  if (itemError || !item) {
    return <ErrorState message={itemError?.message || 'Content item not found'} />
  }

  const TypeIcon = TYPE_ICONS[item.type] || FileText
  const typeColor = TYPE_COLORS[item.type] || 'from-gray-400 to-gray-600'
  const typeLabel = TYPE_LABELS[item.type] || item.type

  const handleEdit = () => {
    navigate(`/content-hub/item/${itemId}/edit`)
  }

  const handleCopyContent = async () => {
    // Copy content based on type
    let contentToCopy = ''

    if (item.textContent?.content) {
      contentToCopy = item.textContent.content
    } else if (item.dataPointContent?.currentValue) {
      contentToCopy = item.dataPointContent.currentValue
    } else if (item.emailSnippetContent?.body) {
      contentToCopy = item.emailSnippetContent.body
    }

    if (contentToCopy) {
      try {
        await navigator.clipboard.writeText(contentToCopy)
        toast({
          title: 'Copied',
          description: 'Content copied to clipboard',
        })
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to copy content',
          variant: 'destructive',
        })
      }
    } else {
      toast({
        title: 'No content',
        description: 'This content type cannot be copied as text',
        variant: 'destructive',
      })
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast({
        title: 'Link copied',
        description: 'Content link copied to clipboard',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to copy link',
        variant: 'destructive',
      })
    }
  }

  const handleDelete = () => {
    deleteItem.mutate(itemId!)
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link
        to="/content-hub"
        className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Content Hub
      </Link>

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className={cn('p-3 rounded-2xl bg-gradient-to-br shadow-lg', typeColor)}>
            <TypeIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{item.name}</h1>
            {item.description && (
              <p className="text-neutral-500 dark:text-neutral-400 mt-1">{item.description}</p>
            )}
            <div className="flex items-center gap-3 mt-3">
              <span
                className={cn(
                  'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r text-white',
                  typeColor
                )}
              >
                {typeLabel}
              </span>
              {item.folder && (
                <Link
                  to={`/content-hub/folder/${item.folder.id}`}
                  className="inline-flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {item.folder.name}
                </Link>
              )}
              {!item.isActive && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                  Inactive
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyContent}
            className="rounded-full gap-2"
          >
            <Copy className="h-4 w-4" />
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyLink}
            className="rounded-full gap-2"
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
          <PermissionGate permissions={['content.update']}>
            <Button
              onClick={handleEdit}
              className="rounded-full gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 hover:bg-neutral-800 dark:hover:bg-neutral-100"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          </PermissionGate>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="rounded-full">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setDuplicateDialogOpen(true)}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setMoveDialogOpen(true)}>
                <Move className="h-4 w-4 mr-2" />
                Move to Folder
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <PermissionGate permissions={['content.delete']}>
                <DropdownMenuItem
                  onClick={() => setDeleteDialogOpen(true)}
                  className="text-red-600 dark:text-red-400"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </PermissionGate>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Content Preview */}
        <FluentCard className="lg:col-span-2">
          <Tabs defaultValue="preview" className="w-full">
            <TabsList className="inline-flex h-11 items-center justify-start gap-1 rounded-xl bg-neutral-100 dark:bg-neutral-800 p-1">
              <TabsTrigger
                value="preview"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-neutral-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-neutral-700 dark:data-[state=active]:text-white"
              >
                Preview
              </TabsTrigger>
              <TabsTrigger
                value="usage"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-neutral-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-neutral-700 dark:data-[state=active]:text-white"
              >
                Usage ({item.usageHistory?.length || 0})
              </TabsTrigger>
              <TabsTrigger
                value="versions"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-neutral-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-neutral-700 dark:data-[state=active]:text-white"
              >
                Versions ({item.version})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="preview" className="mt-6">
              <ContentItemPreview item={item} showFullContent />
            </TabsContent>

            <TabsContent value="usage" className="mt-6">
              <UsageTab usageHistory={item.usageHistory} />
            </TabsContent>

            <TabsContent value="versions" className="mt-6">
              <VersionsTab version={item.version} />
            </TabsContent>
          </Tabs>
        </FluentCard>

        {/* Sidebar */}
        <div className="space-y-6">
          <ContentItemMetadata item={item} usageStats={usageStats} />
          <TagsSection tags={item.tags} onEditTags={handleEdit} />
        </div>
      </div>

      {/* Dialogs */}
      <DeleteItemDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        itemName={item.name}
        onConfirm={handleDelete}
        isDeleting={deleteItem.isPending}
      />

      <MoveItemDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        itemId={item.id}
        itemName={item.name}
        currentFolderId={item.folderId || null}
      />

      <DuplicateItemDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
        itemId={item.id}
        itemName={item.name}
      />
    </div>
  )
}
